import { useState, useEffect } from 'react'
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { useThemeColors } from '../../lib/useThemeColors'
import { fonts } from '../../lib/theme'
import { cacheGet, cacheSet, CACHE_KEY } from './cache'
import { EmptyState, SectionLabel } from './ui'
import { Podio, ListaClassifica, tiedPositions } from './Classifica'
import SegmentedTabs from '../SegmentedTabs'
import { ListSkeleton, ErrorState, StaleBanner } from '../StateViews'
import { fmtGap } from '../Pick/odrResults'

export default function ClassificaTeam({ leagueId, idStage, stageNumber, isOneDay, onTeamClick }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const [scope, setScope] = useState('tappa')
  const [dataT, setDataT] = useState([])
  const [dataG, setDataG] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    if (!idStage || !leagueId) return
    let cancelled = false
    const load = async () => {
      const cacheKey = CACHE_KEY(leagueId, idStage, 'team')
      const cached = await cacheGet(cacheKey)
      if (cached) { setDataT(cached.tappa ?? []); setDataG(cached.generale ?? []); setLoading(false) }
      else setLoading(true)

      const [tRes, gRes] = await Promise.all([
        supabase.rpc('fn_classifica_team', { p_id_league: leagueId, p_id_stage: idStage }),
        supabase.rpc('fn_classifica_team_generale', { p_id_league: leagueId }).select('*').eq('id_stage', idStage).order('posizione_gc'),
      ])
      if (cancelled) return
      if (tRes.error || gRes.error) { setError(true); setLoading(false); return }   // resta quello in cache, se c'e'
      setError(false)

      const resultT = (tRes.data ?? []).map(r => ({ ...r, posizione_classifica: r.posizione }))
      const resultG = (gRes.data ?? []).map(r => ({ ...r, valore: r.gap_totale, posizione_classifica: r.posizione_gc }))

      setDataT(resultT)
      setDataG(resultG)
      setLoading(false)
      cacheSet(cacheKey, { tappa: resultT, generale: resultG })
    }
    load()
    return () => { cancelled = true }
  }, [leagueId, idStage, reloadKey])

  const retry = () => { setLoading(true); setReloadKey(k => k + 1) }
  if (loading) return <ListSkeleton />
  if (error && dataT.length === 0 && dataG.length === 0) return <ErrorState onRetry={retry} />

  const isTappa = scope === 'tappa'
  const data = isTappa ? dataT : dataG

  if (data.length === 0) return (
    <View style={{ padding: 40, alignItems: 'center' }}>
      <Text style={{ color: tc.textMuted, fontSize: 13, textAlign: 'center' }}>{t('leaderboard.team_no_data')}</Text>
    </View>
  )

  const rest = data.slice(3)
  const tied = tiedPositions(data)
  // gara di un giorno: punti; tappe e generale: distacco come +0:12 (0:00 per chi comanda)
  const valueFmt = isOneDay ? (r) => `${r.valore} pt` : (r) => fmtGap(r.valore) || '0:00'
  const stageLabel = isTappa
    ? t('leaderboard.stage', { n: stageNumber }).toUpperCase()
    : t('leaderboard.general').toUpperCase()

  return (
    <View>
      {error && <StaleBanner onRetry={retry} />}
      {/* Tappa / Generale: stesso componente di ClassificaTour */}
      <SegmentedTabs
        options={[{ key: 'tappa', label: t('leaderboard.stage', { n: stageNumber }) }, { key: 'generale', label: t('leaderboard.general') }]}
        value={scope}
        onChange={setScope}
        style={{ marginHorizontal: 16, marginBottom: 12 }}
      />

      <View style={[styles.card, { backgroundColor: tc.bgCard, borderColor: tc.border }]}>
        <View style={{ marginBottom: 16 }}>
          <SectionLabel>{t('leaderboard.team_title')} — {stageLabel}</SectionLabel>
        </View>
        <Podio data={data} nameKey="team_name" valueKey="valore" valueFmt={valueFmt} tied={tied} userIdKey="id_league_team" onPlayerClick={onTeamClick} />
        {rest.length > 0 && (
          <ListaClassifica data={rest} offset={3} nameKey="team_name" valueKey="valore" valueFmt={valueFmt} tied={tied} userIdKey="id_league_team" onPlayerClick={onTeamClick} />
        )}
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  card: { borderRadius: 14, borderWidth: 1, padding: 18, marginHorizontal: 16, marginBottom: 16 },
})